import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import RealTimeAlertPanel from './RealTimeAlertPanel';
import NotificationCenter from './NotificationCenter';
import DemoResetButton from './DemoResetButton';
import { useAuth } from '../../contexts/AuthContext';


const Header = () => {
  const [isOperationsOpen, setIsOperationsOpen] = useState(false);
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const [isFinanceOpen, setIsFinanceOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationBellRef = useRef(null);
  const dropdownRef = useRef(null);
  const salesDropdownRef = useRef(null);
  const financeDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);
  const settingsDropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user, userProfile, loading, isSuperAdmin, isOrgAdmin, canViewMenu } = useAuth();

  const salesItems = [
    { label: 'Quote Management', path: '/quote-management', icon: 'FileText' },
    { label: 'Customer Pricing', path: '/customer-price-list-management', icon: 'Tag' },
    { label: 'Customer Quote Portal', path: '/customer-quote-portal', icon: 'Globe' },
    { label: 'Customer Ticket Portal', path: '/customer-ticket-portal', icon: 'ClipboardCheck' },
    { label: 'Customer Approval Portal', path: '/customer-approval-portal', icon: 'UserCheck' },
  ];

  const operationsItems = [
    { label: 'Support Request Management', path: '/support-request-management', icon: 'Headphones' },
    { label: 'Technician Assignment', path: '/technician-assignment', icon: 'UserCog' },
    { label: 'Ticket Management', path: '/ticket-management', icon: 'ClipboardList' },
    { label: 'Mobile Technician App', path: '/mobile-technician-app', icon: 'Smartphone' }
  ];

  const financeItems = [
    { label: 'Invoice Management', path: '/invoice-management-center', icon: 'DollarSign' },
    { label: 'Invoice Reconciliation', path: '/invoice-reconciliation-dashboard', icon: 'GitMerge' },
    { label: 'Payment Reminders', path: '/payment-reminders-dunning', icon: 'Bell' },
    { label: 'Failed Payment Recovery', path: '/failed-payment-recovery', icon: 'AlertTriangle' },
    { label: 'Invoice & Quote Templates', path: '/invoice-template-customization', icon: 'Palette' },
    { label: 'Recurring Billing', path: '/recurring-billing', icon: 'RefreshCw' },
    { label: 'Accounting Sync', path: '/accounting-sync', icon: 'BookOpen' },
    { label: 'Customer Payment Portal', path: '/customer-payment-portal', icon: 'CreditCard' },
  ];

  const reportsItems = [
    { label: 'All Reports', path: '/reports', icon: 'BarChart3' },
    { label: 'Performance Reports', path: '/reports', icon: 'TrendingUp' },
    { label: 'Saved Templates', path: '/reports', icon: 'Bookmark' },
    { label: 'Analytics', path: '/reports', icon: 'PieChart' },
    { label: 'Approval Dashboard', path: '/approval-dashboard', icon: 'ClipboardCheck' },
    { label: 'Approval Analytics', path: '/approval-analytics-reports', icon: 'BarChart2' },
  ];

  const dataManagementItems = [
    { label: 'Customer Database', path: '/customer-database', icon: 'Users' },
    { label: 'Personnel Management', path: '/personnel-management', icon: 'UserCheck' },
    { label: 'Products', path: '/products', icon: 'Package' },
    { label: 'Inventory Tracking', path: '/inventory-tracking-dashboard', icon: 'Warehouse' },
    { label: 'Inventory Analytics', path: '/inventory-analytics-dashboard', icon: 'BarChart3' }
  ];

  const settingsItems = [
    { label: 'Organization Settings', path: '/organization-settings', icon: 'Building2' },
    { label: 'Company Users', path: '/company-users', icon: 'Users' },
    { label: 'Subscription & Billing', path: '/subscription-billing', icon: 'CreditCard' },
    { label: 'Email Templates', path: '/email-templates', icon: 'Mail' },
    { label: 'Tax Rate Settings', path: '/tax-rate-settings', icon: 'Percent' },
    { label: 'Role Permissions', path: '/role-permissions-settings', icon: 'ShieldCheck' },
    { label: 'Permission Management', path: '/permission-management', icon: 'Shield' },
    { label: 'API Key Management', path: '/api-key-management', icon: 'Key' },
    { label: 'System Health', path: '/system-health-dashboard', icon: 'Activity' },
    { label: 'Data Export & Backup', path: '/data-export-backup', icon: 'Download' },
    { label: 'Document Templates', path: '/document-templates-manager', icon: 'FileStack' },
    { label: 'Workflow Automation', path: '/workflow-automation-center', icon: 'Zap' },
    { label: 'Admin Dashboard', path: '/admin-operator-dashboard', icon: 'Shield' },
    { label: 'Org Dashboard', path: '/organization-dashboard', icon: 'Building2' },
    { label: 'Audit Log', path: '/audit-activity-log', icon: 'Activity' },
    { label: 'Security Audit Log', path: '/security-audit-log', icon: 'ShieldAlert' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOperationsOpen(false);
      }
      if (salesDropdownRef?.current && !salesDropdownRef?.current?.contains(event?.target)) {
        setIsSalesOpen(false);
      }
      if (financeDropdownRef?.current && !financeDropdownRef?.current?.contains(event?.target)) {
        setIsFinanceOpen(false);
      }
      if (reportsDropdownRef?.current && !reportsDropdownRef?.current?.contains(event?.target)) {
        setIsReportsOpen(false);
      }
      if (settingsDropdownRef?.current && !settingsDropdownRef?.current?.contains(event?.target)) {
        setIsSettingsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOperationsOpen(false);
        setIsSalesOpen(false);
        setIsFinanceOpen(false);
        setIsReportsOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsOperationsOpen(false);
    setIsSalesOpen(false);
    setIsFinanceOpen(false);
    setIsReportsOpen(false);
  }, [location?.pathname]);

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const isSalesActive = () => {
    return salesItems?.some(item => location?.pathname === item?.path);
  };

  const isOperationsActive = () => {
    return operationsItems?.some(item => location?.pathname === item?.path);
  };

  const isFinanceActive = () => {
    return financeItems?.some(item => location?.pathname === item?.path);
  };

  const isReportsActive = () => {
    return location?.pathname === '/reports';
  };

  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
    setIsOperationsOpen(false);
    setIsSalesOpen(false);
    setIsFinanceOpen(false);
    setIsReportsOpen(false);
  };

  const toggleSales = () => {
    setIsSalesOpen(!isSalesOpen);
    setIsOperationsOpen(false);
    setIsFinanceOpen(false);
    setIsReportsOpen(false);
  };

  const toggleOperations = () => {
    setIsOperationsOpen(!isOperationsOpen);
    setIsSalesOpen(false);
    setIsFinanceOpen(false);
    setIsReportsOpen(false);
  };

  const toggleFinance = () => {
    setIsFinanceOpen(!isFinanceOpen);
    setIsOperationsOpen(false);
    setIsSalesOpen(false);
    setIsReportsOpen(false);
  };

  const toggleReports = () => {
    setIsReportsOpen(!isReportsOpen);
    setIsOperationsOpen(false);
    setIsFinanceOpen(false);
    setIsSalesOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-card shadow-elevation-2 z-[1000]">
        <div className="h-full px-4 lg:px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="flex items-center gap-3 transition-smooth hover:opacity-80">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Wrench" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground hidden sm:block">
                RIGJOCKEY
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {canViewMenu('dashboard') && (
              <Link
                to="/dashboard"
                className={`px-4 py-2 rounded-md font-medium transition-smooth ${
                  isActiveRoute('/dashboard')
                    ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                }`}
              >
                Dashboard
              </Link>
              )}

              {canViewMenu('myDashboard') && (
              <Link
                to="/my-dashboard"
                className={`px-4 py-2 rounded-md font-medium transition-smooth ${
                  isActiveRoute('/my-dashboard')
                    ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                }`}
              >
                My Dashboard
              </Link>
              )}

              {canViewMenu('sales') && (
              <div className="relative" ref={salesDropdownRef}>
                <button
                  onClick={toggleSales}
                  className={`px-4 py-2 rounded-md font-medium transition-smooth flex items-center gap-2 ${
                    isSalesActive()
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Sales
                  <Icon 
                    name={isSalesOpen ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>

                {isSalesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-popover rounded-lg shadow-elevation-3 py-2 z-[1100]">
                    {salesItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-3 transition-smooth ${
                          isActiveRoute(item?.path)
                            ? 'bg-muted text-primary' :'text-popover-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} color="currentColor" />
                        <span className="font-medium">{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {canViewMenu('operations') && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={toggleOperations}
                  className={`px-4 py-2 rounded-md font-medium transition-smooth flex items-center gap-2 ${
                    isOperationsActive()
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Operations
                  <Icon 
                    name={isOperationsOpen ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>

                {isOperationsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-popover rounded-lg shadow-elevation-3 py-2 z-[1100]">
                    {operationsItems?.map((item) => (
                      <Link
                        key={item?.path + item?.label}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-3 transition-smooth ${
                          isActiveRoute(item?.path)
                            ? 'bg-muted text-primary' :'text-popover-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} color="currentColor" />
                        <span className="font-medium">{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Finance Dropdown */}
              {canViewMenu('finance') && (
              <div className="relative" ref={financeDropdownRef}>
                <button
                  onClick={toggleFinance}
                  className={`px-4 py-2 rounded-md font-medium transition-smooth flex items-center gap-2 ${
                    isFinanceActive()
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Finance
                  <Icon
                    name={isFinanceOpen ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                    color="currentColor"
                  />
                </button>

                {isFinanceOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-popover rounded-lg shadow-elevation-3 py-2 z-[1100]">
                    {financeItems?.map((item) => (
                      <Link
                        key={item?.path + item?.label}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-3 transition-smooth ${
                          isActiveRoute(item?.path)
                            ? 'bg-muted text-primary' :'text-popover-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} color="currentColor" />
                        <span className="font-medium">{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Reports Dropdown */}
              {canViewMenu('reports') && (
              <div className="relative" ref={reportsDropdownRef}>
                <button
                  onClick={toggleReports}
                  className={`px-4 py-2 rounded-md font-medium transition-smooth flex items-center gap-2 ${
                    isReportsActive()
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Reports
                  <Icon
                    name={isReportsOpen ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                    color="currentColor"
                  />
                </button>

                {isReportsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-popover rounded-lg shadow-elevation-3 py-2 z-[1100]">
                    {reportsItems?.map((item) => (
                      <Link
                        key={item?.label}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-3 transition-smooth ${
                          isActiveRoute(item?.path)
                            ? 'bg-muted text-primary' :'text-popover-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} color="currentColor" />
                        <span className="font-medium">{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {canViewMenu('dataManagement') && (
              <Link
                to="/data-management"
                className={`px-4 py-2 rounded-md font-medium transition-smooth ${
                  isActiveRoute('/data-management') || isActiveRoute('/customer-database') || isActiveRoute('/personnel-management') || isActiveRoute('/products') || isActiveRoute('/inventory-tracking-dashboard')
                    ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                }`}
              >
                Data Management
              </Link>
              )}

              {/* Settings Dropdown */}
              {canViewMenu('settings') && (
              <div className="relative" ref={settingsDropdownRef}>
                <button
                  onClick={toggleSettings}
                  className={`px-4 py-2 rounded-md font-medium transition-smooth flex items-center gap-2 ${
                    settingsItems?.some(item => location?.pathname === item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Settings" size={16} color="currentColor" />
                  Settings
                  <Icon
                    name={isSettingsOpen ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                    color="currentColor"
                  />
                </button>
                {isSettingsOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-popover rounded-lg shadow-elevation-3 py-2 z-[1100]">
                    {settingsItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-3 transition-smooth ${
                          isActiveRoute(item?.path)
                            ? 'bg-muted text-primary' :'text-popover-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} color="currentColor" />
                        <span className="font-medium">{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Superadmin Platform Link */}
              {isSuperAdmin && (
                <Link
                  to="/superadmin-organizations"
                  className={`px-4 py-2 rounded-md font-medium transition-smooth flex items-center gap-1.5 ${
                    isActiveRoute('/superadmin-organizations')
                      ? 'bg-purple-600 text-white' :'text-purple-600 hover:bg-purple-50 border border-purple-200'
                  }`}
                >
                  <Icon name="Shield" size={15} color="currentColor" />
                  Platform
                </Link>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* Demo Reset Button */}
            <div className="hidden lg:block">
              <DemoResetButton onReset={() => {}} />
            </div>

            {/* Notification Center Bell */}
            <div className="relative" ref={notificationBellRef}>
              <button
                className="relative p-2 rounded-lg hover:bg-muted transition-smooth"
                onClick={() => {
                  setIsNotificationCenterOpen(prev => !prev);
                  setIsAlertPanelOpen(false);
                }}
              >
                <Icon name="Bell" size={20} color="var(--color-foreground)" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <NotificationCenter
                isOpen={isNotificationCenterOpen}
                onClose={() => setIsNotificationCenterOpen(false)}
                onUnreadCountChange={setUnreadCount}
              />
            </div>

            <RealTimeAlertPanel isOpen={isAlertPanelOpen} onClose={() => setIsAlertPanelOpen(false)} />

            <button
              onClick={handleSignOut}
              title="Sign Out"
              className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
            >
              <Icon name="LogOut" size={16} color="currentColor" />
              <span>Sign Out</span>
            </button>

            <button className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth" onClick={toggleMobileMenu}>
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} color="var(--color-foreground)" />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-16 bg-card z-[1300] lg:hidden overflow-y-auto">
          <nav className="p-4 space-y-2">
            <Link
              to="/dashboard"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                isActiveRoute('/dashboard')
                  ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="LayoutDashboard" size={20} color="currentColor" />
              Dashboard
            </Link>

            <Link
              to="/my-dashboard"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                isActiveRoute('/my-dashboard')
                  ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="User" size={20} color="currentColor" />
              My Dashboard
            </Link>

            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground">
                Sales
              </div>
              {salesItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActiveRoute(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  {item?.label}
                </Link>
              ))}
            </div>

            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground">
                Operations
              </div>
              {operationsItems?.map((item) => (
                <Link
                  key={item?.path + item?.label}
                  to={item?.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActiveRoute(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  {item?.label}
                </Link>
              ))}
            </div>

            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground">
                Finance
              </div>
              {financeItems?.map((item) => (
                <Link
                  key={item?.path + item?.label}
                  to={item?.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActiveRoute(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  {item?.label}
                </Link>
              ))}
            </div>

            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground">
                Reports
              </div>
              <Link
                to="/reports"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                  isReportsActive()
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="BarChart3" size={20} color="currentColor" />
                All Reports
              </Link>
            </div>

            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground">
                Data Management
              </div>
              {dataManagementItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActiveRoute(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  {item?.label}
                </Link>
              ))}
            </div>

            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground">
                Settings
              </div>
              {settingsItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActiveRoute(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  {item?.label}
                </Link>
              ))}
            </div>

            {isSuperAdmin && (
              <div className="space-y-1">
                <div className="px-4 py-2 text-sm font-caption font-medium text-purple-600">
                  Superadmin
                </div>
                <Link
                  to="/superadmin-organizations"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActiveRoute('/superadmin-organizations')
                      ? 'bg-purple-600 text-white' :'text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Icon name="Shield" size={20} color="currentColor" />
                  Platform Organizations
                </Link>
              </div>
            )}
          </nav>
        </div>
      )}
      <div className="h-16" />
    </>
  );
};

export default Header;